import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Divider,
    InputAdornment,
    IconButton,
    Chip,
} from '@mui/material';
import {
    Save as SaveIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    NetworkCheck as NetworkCheckIcon,
    CloudQueue as CloudQueueIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/api';

const FIELDS = [
    {
        key: 'CLOUDFLARE_ACCOUNT_ID',
        label: 'Cloudflare Account ID',
        placeholder: 'e.g. a1b2c3d4e5f6...',
        hint: 'Found in the Cloudflare dashboard URL or right sidebar.',
        secret: false,
    },
    {
        key: 'R2_ACCESS_KEY_ID',
        label: 'R2 Access Key ID',
        placeholder: 'e.g. abc123...',
        hint: 'Create R2 API tokens at Cloudflare → R2 → Manage API tokens.',
        secret: false,
    },
    {
        key: 'R2_SECRET_ACCESS_KEY',
        label: 'R2 Secret Access Key',
        placeholder: 'Leave blank to keep existing secret',
        hint: 'Only shown once when the token is created. Re-enter to update.',
        secret: true,
    },
    {
        key: 'R2_BUCKET_NAME',
        label: 'R2 Bucket Name',
        placeholder: 'e.g. socialixer-media',
        hint: 'The name of your R2 bucket.',
        secret: false,
    },
    {
        key: 'R2_PUBLIC_URL',
        label: 'R2 Public URL',
        placeholder: 'e.g. https://media.socialixer.com',
        hint: 'Public base URL for the bucket (custom domain or R2.dev subdomain). No trailing slash.',
        secret: false,
    },
];

export default function AdminR2Settings() {
    const [values, setValues] = useState({
        CLOUDFLARE_ACCOUNT_ID: '',
        R2_ACCESS_KEY_ID: '',
        R2_SECRET_ACCESS_KEY: '',
        R2_BUCKET_NAME: '',
        R2_PUBLIC_URL: '',
    });
    const [showSecret, setShowSecret] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [connectionStatus, setConnectionStatus] = useState(null); // null | true | false

    const fetchConfig = useCallback(async () => {
        try {
            setLoading(true);
            const data = await adminApi.getR2Config();
            setValues({
                CLOUDFLARE_ACCOUNT_ID: data.CLOUDFLARE_ACCOUNT_ID || '',
                R2_ACCESS_KEY_ID: data.R2_ACCESS_KEY_ID || '',
                R2_SECRET_ACCESS_KEY: data.R2_SECRET_ACCESS_KEY || '',   // will be '••••••••' if set
                R2_BUCKET_NAME: data.R2_BUCKET_NAME || '',
                R2_PUBLIC_URL: data.R2_PUBLIC_URL || '',
            });
        } catch (err) {
            setError(err.message || 'Failed to load R2 configuration');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchConfig(); }, [fetchConfig]);

    const handleChange = (key) => (e) => {
        setValues(prev => ({ ...prev, [key]: e.target.value }));
        setSuccess('');
        setConnectionStatus(null);
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        setConnectionStatus(null);
        try {
            const result = await adminApi.updateR2Config(values);
            setSuccess('R2 configuration saved successfully.');
            setConnectionStatus(result.connected);
            // Reload to get masked secret
            await fetchConfig();
        } catch (err) {
            setError(err.message || 'Failed to save R2 configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async () => {
        setTesting(true);
        setError('');
        setSuccess('');
        setConnectionStatus(null);
        try {
            const result = await adminApi.testR2Connection();
            setConnectionStatus(result.connected);
            if (result.connected) {
                setSuccess('Connection test passed — R2 is reachable.');
            } else {
                setError(result.message || 'R2 connection test failed. Check your credentials.');
            }
        } catch (err) {
            setConnectionStatus(false);
            setError(err.message || 'Connection test failed');
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 720, mx: 'auto', p: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CloudQueueIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>R2 Storage Settings</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Cloudflare R2 credentials for media and blog image storage
                        </Typography>
                    </Box>
                </Box>
                {connectionStatus !== null && (
                    <Chip
                        label={connectionStatus ? 'Connected' : 'Not Connected'}
                        color={connectionStatus ? 'success' : 'error'}
                        size="small"
                    />
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

            <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        Credentials
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        These values are stored in Firestore (<code>settings/r2_config</code>) and override
                        any environment variables at runtime.
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {FIELDS.map(({ key, label, placeholder, hint, secret }) => (
                            <Box key={key}>
                                <TextField
                                    fullWidth
                                    label={label}
                                    placeholder={placeholder}
                                    value={values[key]}
                                    onChange={handleChange(key)}
                                    type={secret && !showSecret ? 'password' : 'text'}
                                    size="small"
                                    inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                                    InputProps={secret ? {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setShowSecret(s => !s)}
                                                    edge="end"
                                                    aria-label={showSecret ? 'Hide secret' : 'Show secret'}
                                                >
                                                    {showSecret ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    } : undefined}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    {hint}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            disableElevation
                            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                            onClick={handleSave}
                            disabled={saving || testing}
                        >
                            Save Changes
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={testing ? <CircularProgress size={18} /> : <NetworkCheckIcon />}
                            onClick={handleTestConnection}
                            disabled={saving || testing}
                        >
                            Test Connection
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Help card */}
            <Card sx={{ mt: 3, borderRadius: 3, bgcolor: 'action.hover' }}>
                <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        How to get R2 credentials
                    </Typography>
                    <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75, fontSize: '0.85rem', color: 'text.secondary' } }}>
                        <li>Log in to the <strong>Cloudflare dashboard</strong> and open your account.</li>
                        <li>Go to <strong>R2 Object Storage</strong> and create a bucket (e.g. <code>socialixer-media</code>).</li>
                        <li>In bucket settings, enable a <strong>Public access</strong> custom domain or R2.dev subdomain — that becomes <em>R2 Public URL</em>.</li>
                        <li>Navigate to <strong>R2 → Manage API tokens</strong> and create a token with <em>Object Read &amp; Write</em> permission.</li>
                        <li>Copy the <strong>Access Key ID</strong> and <strong>Secret Access Key</strong> from the token page (shown only once).</li>
                        <li>Your <strong>Account ID</strong> is in the right sidebar of the Cloudflare dashboard home.</li>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
